import { Avatar } from "@themis/ui";
import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import Popup from "reactjs-popup";

import UserAvatar from "@/components/UserAvatar";
import { useMainStore } from "@/contexts/Store";
import { User } from "@/stores/types/user-types";

const DEFAULT_USER_COUNT = 3;

function ViewModuleUsers() {
  // Import MobX stores
  const mainStore = useMainStore();

  // State
  const [users, setUsers] = useState<User[]>([]);
  const [showPopup, setShowPopup] = useState(false);

  // Variables
  const { themisModuleIdentifier } = mainStore.context;
  const { list: workspaces } = mainStore.moduleWorkspaces;

  const moduleWorkspace = useMemo(
    () =>
      workspaces.find(
        (mws) => mws.themis_module.identifier === themisModuleIdentifier,
      ),
    [workspaces, themisModuleIdentifier],
  );

  // Hooks
  useEffect(() => {
    if (moduleWorkspace) {
      getUsers();
    }
  }, [moduleWorkspace]);

  // Functions
  async function getUsers() {
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    if (!moduleWorkspace.id) {
      return;
    }

    try {
      const res = await mainStore.permissions.getUsersAccessibleToModule(
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        moduleWorkspace.id,
      );
      setUsers(res);
    } catch {
      setUsers([]);
    }
  }

  const popup = (
    <Popup
      position="bottom left"
      trigger={
        <Avatar
          size="lg"
          color="gray"
          variant="basic"
          className="tw-absolute tw-cursor-pointer"
          style={{
            left: `${24 * DEFAULT_USER_COUNT}px`,
          }}
        >
          +{users.length - DEFAULT_USER_COUNT}
        </Avatar>
      }
      open={showPopup}
      keepTooltipInside
      onOpen={() => setShowPopup(true)}
      onClose={() => setShowPopup(false)}
    >
      <div className="module-users-popup">
        <div className="module-users-popup-header">Module Users</div>
        <div className="module-users-popup-content">
          {users?.slice(DEFAULT_USER_COUNT).map((user) => (
            <div
              key={user.id}
              className={classNames("module-users-popup-item", {
                "awaiting-verification": user.awaiting_verification,
              })}
            >
              <UserAvatar user={user} size="sm" showName />
              {user.awaiting_verification && (
                <div className="module-users-popup-item-verification-status">
                  {user.status}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Popup>
  );

  return (
    <div
      className="tw-relative tw-ml-3 tw-h-7"
      style={{
        width:
          users.length > DEFAULT_USER_COUNT
            ? "100px"
            : `${25 * users.length}px`,
      }}
      data-testid="view-module-users"
    >
      {users.slice(0, DEFAULT_USER_COUNT).map((user, index) => (
        <Avatar
          key={user.id}
          size="lg"
          colorIndex={user.icon_color_index}
          className="tw-absolute tw-cursor-default"
          style={{
            left: `${24 * index}px`,
          }}
        >
          {user.initials}
        </Avatar>
      ))}
      {users.length > DEFAULT_USER_COUNT && popup}
    </div>
  );
}

export default observer(ViewModuleUsers);
